import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, LOCALE_ID, OnDestroy, signal, ViewChild, OnInit, inject } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { LoggingService, NavigationService, UrlService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IntentService } from "@tylertech/intents-module";
import { Utilities } from "@tylertech/shared-module";
import { AppLinkParms, ExecutionResult, FilterItem, IChartCriteria, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "purchasing-open-purchase-orders-by-department-chart",
    templateUrl: "./openPurchaseOrdersByDepartmentChart.tmpl.html",
    styleUrls: [
        "./openPurchaseOrdersByDepartmentChart.component.scss"
    ],
    standalone: false
})
export class OpenPurchaseOrdersByDepartmentChartComponent implements OnDestroy, ICustomViewComponent<ChartBindingObject>, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $chartService = inject(ChartService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $urlService = inject(UrlService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $intentService = inject(IntentService);

    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    readonly customDataBinding: ChartBindingObject;

    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    readonly ChartTitle: string = "Open Purchase Orders by Department";
    ChartSubtitle: string;

    private _chartCriteria: IChartCriteria;
    isLoaded: boolean;

    total: string;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
    }

    get ariaLabel(): string { return `Card-${this.ChartTitle}Chart-Purchasing`; }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private updateChartConfiguration(): void
    {
        const amount = this.chartCriteria.ChartDatas[0].Data.reduce((a, v) => a + v.Total, 0);
        this.total = formatCurrency(amount, this.locale, "$");

        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                let label = tooltipItem.label;
                                if (this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].FilterParam)
                                {
                                    label = `${this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].FilterParam.Value}`;
                                }

                                const count = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].Count;
                                const value = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].Total;
                                return `${label} ( Count: ${count}; Total: ${formatCurrency(value, this.locale, "$")} )`;
                            },
                        },
                    },
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (Utilities.IsNullOrUndefined(element))
                    {
                        return;
                    }
                    const index = element.index;
                    const filter: FilterItem[] = [];
                    if (!Utilities.IsNullOrUndefined(this.chartCriteria.ChartDatas[0].Data[index].FilterParam))
                    {
                        filter.push(this.chartCriteria.ChartDatas[0].Data[index].FilterParam);
                    }

                    else
                    {
                        filter.push(<FilterItem>{
                            Key: "Department",
                            Value: this.chartCriteria.ChartDatas[0].Labels[index],
                        });
                    }
                    if (this.chartCriteria.ChartDatas[0].Additional && this.chartCriteria.ChartDatas[0].Additional[0])
                    {
                        filter.push(this.chartCriteria.ChartDatas[0].Additional[0]);
                    }
                    const app = "purchaseorders";
                    const parms: AppLinkParms = {
                        App: app,
                        Parms: filter,
                    };
                    this.$urlService.GetAppLink(parms)
                        .then((executionResult: ExecutionResult<string>) =>
                        {
                            const index = executionResult.Result.indexOf("?");
                            if (executionResult.Result.includes("apps/form"))
                            {
                                this.$navigationService.navigateToApp(executionResult.Result);
                            }
                            else if (index > 0)
                            {
                                this.$intentService.resolve("PurchaseOrders", "Search", {
                                    querystring: executionResult.Result.substring(index, executionResult.Result.length),
                                });
                            }
                        });
                },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        //  id: "y-axis-1",
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                datasets: [
                    {
                        type: "bar",
                        data: this.chartCriteria.ChartDatas[0].Data.map(x => x.Total),
                        backgroundColor: this.$chartService.getColors(this.chartCriteria.ChartDatas[0].Data.length),
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    }
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
